/**
 * ユーザーの表示名取得ヘルパー。
 *
 * `/api/me` および `/api/me/follows` / `/api/roles/<id>/users` のレスポンスでは
 * 表示名が `display_name_jp` / `display_name_en` の 2 系統で並列に返るため、
 * jp 優先で空文字をスキップしながらフォールバックを取る処理を一箇所に集約する。
 *
 * 旧フィールド `display_name` / `stage_name` を参照しているコードを置き換える
 * 際の中心的な API になる。
 */
type NamedUser = {
  display_name_jp?: string | null;
  display_name_en?: string | null;
};

const pickNonEmpty = (value: string | null | undefined): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? value : null;
};

/**
 * ユーザーの表示名を取得する。
 *
 * @param user 対象ユーザー（`display_name_jp` / `display_name_en` のみ参照）
 * @param lang 優先言語。既定は `"jp"`。空文字は弾き、もう一方にフォールバックする。
 * @returns 表示名。両方空のときは空文字列。
 */
export const getUserDisplayName = (
  user: NamedUser | null | undefined,
  lang: "jp" | "en" = "jp",
): string => {
  if (!user) return "";
  const jp = pickNonEmpty(user.display_name_jp);
  const en = pickNonEmpty(user.display_name_en);
  const primary = lang === "jp" ? jp : en;
  const secondary = lang === "jp" ? en : jp;
  return primary ?? secondary ?? "";
};
